import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { FileText, Users, LogIn, Eye, MonitorSmartphone, X, Copy, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button, Badge } from '@staff-app/ui';
import type { CustomerResolveResult, DomainMatchResult } from '@staff-app/shared-types';
import { CustomerFacts, CopyBtn } from './CustomerFacts';
import { CustomerSearchBox } from './CustomerSearchBox';
import { CustomerPanel } from './CustomerPanel';
import { RecentCustomers } from './RecentCustomers';
import type { RecentCustomer } from '../stores/recent-customers-store';
import {
  useStaffResolveCustomer,
  useStaffLoginAsCustomer,
  useStaffGetCustomerInfo,
  useStaffGetCustomer,
  useStaffBillingSubscriptionStatus,
} from '../hooks/useStaffCustomer';
import { accountStatus, sortByAccountStatus, parseStripeId, parseMaxScreens, parsePaymentFlags, parsePairedScreens, parseActiveAddOns, searchedFact } from '../lib/customer-info';
import { useCustomersSearchStore } from '../stores/customers-search-store';

/**
 * Synthesize the CustomerResolveResult a card expects from a stored recent.
 * Only accountId is strictly required — the card re-fetches screens/stripe/
 * status/plan live by accountId (same as the panel does), so the stored
 * company/owner are just for the header while that resolves.
 */
function recentToResolved(r: RecentCustomer): CustomerResolveResult {
  return {
    found: true,
    inputEmail: r.ownerEmail ?? r.accountId,
    ownerResolvable: true,
    accountId: r.accountId,
    companyName: r.companyName,
    ownerEmail: r.ownerEmail,
    inputIsOwner: true,
    teams: [],
  };
}

function TeamBadges({ teams }: { teams: CustomerResolveResult['teams'] }) {
  if (!teams?.length) return <span className="text-muted-foreground">{'Default Team'}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {teams.map((t) => (
        <Badge key={t.teamId} variant={t.isCurrent ? 'default' : 'secondary'} className="text-[10px]">
          {t.teamName}{t.isCurrent ? ' ✓' : ''}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Billing facts for the searched user's TEAM-BILLING teams, under the Teams row. A regular
 * team (or the Default Team) shares the root subscription, so it renders nothing — the
 * account-level Stripe id / screens above already describe it. The team name only prints
 * when the user sits on more than one team-billing team, to say which numbers are whose.
 */
function TeamBillingFacts({ teams }: { teams: CustomerResolveResult['teams'] }) {
  const billed = (teams ?? []).filter((t) => t.isTeamBilling);
  if (!billed.length) return null;
  return (
    <div className="flex flex-col gap-1.5 pt-1" data-testid="users-search-team-billing">
      {billed.map((t) => (
        <div key={t.teamId} className="flex flex-col gap-0.5" data-testid="users-search-team-billing-row">
          {(billed.length > 1 || !t.teamBillingActive) && (
            <span className="flex items-center gap-1">
              {billed.length > 1 && <span className="font-medium text-foreground">{t.teamName}</span>}
              {!t.teamBillingActive && (
                <Badge variant="destructive" className="text-[10px]" data-testid="users-search-team-billing-inactive">
                  {'Team billing inactive'}
                </Badge>
              )}
            </span>
          )}
          {t.stripeId && (
            <span className="block">
              <span className="text-foreground">{'Team Stripe id: '}</span>
              <span className="font-mono">{t.stripeId}</span>
              <CopyBtn value={t.stripeId} label="Team Stripe id" testId="users-search-copy-team-stripe-id" stopPropagation />
            </span>
          )}
          <span className="flex flex-wrap gap-x-4">
            <span>
              <span className="text-foreground">{'Team Paired Screens: '}</span>
              {t.pairedScreens ?? '—'}
            </span>
            <span>
              <span className="text-foreground">{'Team Licensed Screens: '}</span>
              {t.licensedScreens ?? '—'}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function LoginBtn({ accountId, small = false }: { accountId: string; small?: boolean }) {
  const [login, { loading }] = useStaffLoginAsCustomer();

  const handleLogin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await login({ variables: { accountId } });
      const url = result.data?.staffLoginAsCustomer?.appUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      toast.error('Login failed', { description: (err as Error)?.message ?? 'Unknown error' });
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={handleLogin} disabled={loading} className={small ? 'h-6 gap-1 text-[11px]' : 'h-7 gap-1 text-xs'} data-testid="account-card-login-btn">
      <LogIn className="h-3 w-3" />
      {loading ? 'Opening…' : 'Login'}
    </Button>
  );
}

/**
 * Opens the customer in the Stripe dashboard — the same link as the panel's Stripe button,
 * so it no longer takes a View & Edit to reach it. Not rendered without a Stripe id.
 */
function StripeBtn({ stripeId, small = false }: { stripeId: string | null; small?: boolean }) {
  if (!stripeId) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={(e) => {
        e.stopPropagation();
        window.open(`https://dashboard.stripe.com/customers/${stripeId}`, '_blank', 'noopener,noreferrer');
      }}
      className={small ? 'h-6 gap-1 text-[11px]' : 'h-7 gap-1 text-xs'}
      data-testid="account-card-stripe-btn"
    >
      <ExternalLink className="h-3 w-3" />
      {'Stripe'}
    </Button>
  );
}

function DeviceListBtn({ accountId, small = false }: { accountId?: string; small?: boolean }) {
  const navigate = useNavigate();
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={!accountId}
      onClick={(e) => {
        e.stopPropagation();
        if (accountId) navigate(`/staff/customers/${encodeURIComponent(accountId)}/devices`);
      }}
      className={small ? 'h-6 gap-1 text-[11px]' : 'h-7 gap-1 text-xs'}
      data-testid="account-card-devices-btn"
    >
      <MonitorSmartphone className="h-3 w-3" />
      {'Device list'}
    </Button>
  );
}

/**
 * Documents — sits beside Device list because both answer "show me this customer's X".
 * Goes to the standalone documents page rather than a customer-360 tab: documents are a
 * whole workflow (upload, type filters, versions), not a panel.
 */
function DocumentsBtn({ accountId, small = false }: { accountId?: string; small?: boolean }) {
  const navigate = useNavigate();
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={!accountId}
      onClick={(e) => {
        e.stopPropagation();
        if (accountId) navigate(`/staff/customers/${encodeURIComponent(accountId)}/documents`);
      }}
      className={small ? 'h-6 gap-1 text-[11px]' : 'h-7 gap-1 text-xs'}
      data-testid="account-card-documents-btn"
    >
      <FileText className="h-3 w-3" />
      {'Documents'}
    </Button>
  );
}

function ResolvedCard({
  resolved,
  onViewEdit,
}: {
  resolved: CustomerResolveResult;
  onViewEdit: () => void;
}) {
  // Same company info the panel loads — stripe_id + maxQuantity aren't on the
  // resolve result. Apollo caches it, so opening the panel after is free.
  const { data: infoData } = useStaffGetCustomerInfo(resolved.accountId ?? null);
  const info = infoData?.staffGetCustomerInfo;
  const stripeId = parseStripeId(info);
  const maxScreens = parseMaxScreens(info);
  // Paired screens = account-wide: limitation.quantity (the "quantity" field in
  // the panel's info editor, screens outside any team) + the teams' paired
  // screens — not the raw device count.
  const pairedScreens = parsePairedScreens(info);
  // Company rollup is still fetched to fill plan/planFeature for lean recents
  // cards (and to warm the Apollo cache for the device drill-in).
  const { data: customerData } = useStaffGetCustomer(resolved.accountId ?? undefined);
  const customer = customerData?.staffGetCustomer;
  // Fall back to the live-fetched company data when the resolve result is a lean
  // synthesized one (a previous-search card carries only accountId). This lets a
  // recents card render the same badges/status as a fresh search.
  const plan = resolved.plan ?? customer?.plan;
  const planFeature = resolved.planFeature ?? customer?.planFeature;
  const flags = parsePaymentFlags(info);
  const status = accountStatus(resolved.isTrial ?? flags.isTrial, resolved.isActive ?? flags.isActive);
  // Active add-ons (videowall / aericast / …). Empty → nothing extra renders.
  const subStatus = useStaffBillingSubscriptionStatus(stripeId).data
    ?.staffBillingSubscriptionStatus;
  const activeAddOns = parseActiveAddOns(info);

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border bg-card p-3 text-xs"
      data-testid="users-search-result"
    >
      {/*
        Heading ONLY when the account has a real name. It used to fall back to the accountId,
        which printed the same 17 characters twice — once here and again as the "Account id"
        row in the facts block directly below. Nothing is lost by dropping it: owner, account
        id and Stripe id are all right there, each with its own copy button.
      */}
      {resolved.companyName ? (
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-semibold text-sm truncate">{resolved.companyName}</span>
        </div>
      ) : null}

      <div className="flex flex-col gap-0.5 text-muted-foreground">
        <CustomerFacts
          prefix="users-search"
          stopPropagation
          status={status}
          plan={plan}
          planFeature={planFeature}
          ownerEmail={resolved.ownerEmail}
          accountId={resolved.accountId}
          stripeId={stripeId}
          subscriptionStatus={subStatus?.status}
          subscriptionMultiple={subStatus?.multiple}
          pairedScreens={pairedScreens}
          maxScreens={maxScreens}
          searched={searchedFact(resolved)}
          teams={<TeamBadges teams={resolved.teams} />}
        >
          <TeamBillingFacts teams={resolved.teams} />
        </CustomerFacts>
        {activeAddOns.length > 0 && (
          <div className="flex flex-wrap items-center gap-1" data-testid="users-search-addons">
            <span className="text-foreground">{'Add-On: '}</span>
            {activeAddOns.map((addOn) => (
              <Badge key={addOn.key} variant="default" className="text-[10px]" data-testid="users-search-addon">
                {addOn.key}
                {addOn.maxQuantity > 0 ? ` · ${addOn.maxQuantity}` : ''}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={onViewEdit} data-testid="account-card-edit-btn">
          <Eye className="h-3 w-3" />
          {'View & Edit'}
        </Button>
        <DeviceListBtn accountId={resolved.accountId} />
        <DocumentsBtn accountId={resolved.accountId} />
        {resolved.accountId && <LoginBtn accountId={resolved.accountId} />}
        <StripeBtn stripeId={stripeId} />
      </div>
    </div>
  );
}

function DomainMatchCard({ match, onViewEdit }: { match: DomainMatchResult; onViewEdit: () => void }) {
  // Same per-account info the panel/card use — stripe_id + maxQuantity aren't on
  // the domain match. Only the (≤5) rows on the current page mount this hook.
  const { data: infoData } = useStaffGetCustomerInfo(match.accountId ?? null);
  const info = infoData?.staffGetCustomerInfo;
  const stripeId = parseStripeId(info);
  const status = accountStatus(match.isTrial, match.isActive);

  return (
    <div
      className="flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2 text-xs cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={onViewEdit}
      data-testid="users-search-result"
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-medium truncate">{match.companyName ?? match.accountId}</span>
        <span className="text-muted-foreground truncate">{match.ownerEmail ?? '—'}</span>
        {stripeId && (
          <span className="truncate text-muted-foreground" data-testid="domain-match-stripe-id">
            <span className="text-foreground">{'Stripe id: '}</span>
            <span className="font-mono">{stripeId}</span>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await navigator.clipboard.writeText(stripeId);
                  toast.success('Stripe id copied');
                } catch {
                  toast.error('Copy failed');
                }
              }}
              aria-label={'Copy Stripe id'}
              title={'Copy Stripe id'}
              data-testid="domain-match-copy-stripe-id"
              className="ml-1.5 inline-flex cursor-pointer items-center align-middle hover:text-foreground"
            >
              <Copy className="h-3 w-3" />
            </button>
          </span>
        )}
        <div className="flex flex-wrap gap-1">
          {match.plan && <Badge variant="secondary" className="text-[10px]">{match.plan}</Badge>}
          {match.planFeature && <Badge variant="outline" className="text-[10px]">{match.planFeature}</Badge>}
          <Badge variant={status.variant} className="text-[10px]" data-testid="domain-match-status">
            {status.label}
          </Badge>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <DeviceListBtn accountId={match.accountId} small />
        <DocumentsBtn accountId={match.accountId} small />
        <LoginBtn accountId={match.accountId} small />
        <StripeBtn stripeId={stripeId} small />
      </div>
    </div>
  );
}

const PAGE_SIZE = 5;

export function UsersSearchColumn() {
  // Query is cached in a store so it survives navigating away (e.g. to My
  // devices) and back, until the user clears it or searches something else.
  const query = useCustomersSearchStore((s) => s.usersQuery);
  const setQuery = useCustomersSearchStore((s) => s.setUsersQuery);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelResolved, setPanelResolved] = useState<CustomerResolveResult | null>(null);
  const [page, setPage] = useState(0);
  // A recently-accessed customer the user clicked: rendered as a card in the
  // column (NOT the panel). Cleared by the card's "back" affordance or by typing.
  const [recentCard, setRecentCard] = useState<CustomerResolveResult | null>(null);
  const { data, loading, error } = useStaffResolveCustomer(query);
  const resolved = data?.staffResolveCustomer ?? null;
  // Multi-account results ordered by status (active → trial → trial end →
  // deactivated) BEFORE pagination, so page 1 leads with the accounts an agent
  // is most likely after.
  const orderedMatches = useMemo(
    () => (resolved?.domainMatches ? sortByAccountStatus(resolved.domainMatches) : null),
    [resolved?.domainMatches],
  );

  // Reset to page 0 whenever query changes
  useEffect(() => { setPage(0); }, [query]);
  // Typing a real search dismisses the recents card.
  useEffect(() => { if (query.trim().length >= 2) setRecentCard(null); }, [query]);

  const openPanel = (r: CustomerResolveResult) => { setPanelResolved(r); setPanelOpen(true); };

  // Clicking a recent shows it as a card in the column — it does NOT open the panel.
  const openRecent = (r: RecentCustomer) => setRecentCard(recentToResolved(r));

  const openPanelForMatch = (match: DomainMatchResult) => openPanel({
    found: true,
    inputEmail: match.ownerEmail ?? match.accountId,
    ownerResolvable: true,
    accountId: match.accountId,
    companyName: match.companyName,
    plan: match.plan,
    planFeature: match.planFeature,
    isTrial: match.isTrial,
    isActive: match.isActive,
    ownerEmail: match.ownerEmail,
    ownerName: match.ownerName,
    inputIsOwner: true,
    teams: [],
  });

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4"
      data-testid="users-search-column"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">{'Users'}</h2>
        </div>
        {query ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 text-xs"
            onClick={() => setQuery('')}
            data-testid="users-search-clear"
          >
            <X className="h-3 w-3" />
            {'Clear'}
          </Button>
        ) : null}
      </header>
      <CustomerSearchBox
        initialValue={query}
        onSubmit={setQuery}
        placeholder={'Email, @domain, cus_…, or account id'}
        ariaLabel={'Search users'}
        testId="users-search-box"
      />

      {error ? (
        <p className="text-xs text-destructive" data-testid="users-search-error">
          {'Search failed.'} {error.message}
        </p>
      ) : loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'Searching…'}</p>
      ) : query.trim().length < 2 ? (
        recentCard ? (
          <div className="flex flex-col gap-2" data-testid="recent-customer-card">
            <button
              type="button"
              onClick={() => setRecentCard(null)}
              className="flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              data-testid="recent-customer-card-back"
            >
              <ArrowLeft className="h-3 w-3" />
              {'Recently accessed'}
            </button>
            <ResolvedCard resolved={recentCard} onViewEdit={() => openPanel(recentCard)} />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <RecentCustomers onOpen={openRecent} />
            <p className="py-2 text-center text-xs text-muted-foreground">{'Type to search…'}</p>
          </div>
        )
      ) : !resolved ? null : resolved.found ? (
        <div className="flex flex-col gap-2">
          <ResolvedCard
            resolved={resolved}
            onViewEdit={() => openPanel(resolved)}
          />
          {/*
            The same email on another account's team (a person with their own account who is
            also a member elsewhere). Without this the search only ever showed one account.
          */}
          {resolved.otherAccounts?.length ? (
            <div className="flex flex-col gap-2" data-testid="users-search-other-accounts">
              <p className="text-xs text-muted-foreground">{'Also a team member of'}</p>
              {sortByAccountStatus(resolved.otherAccounts).map((m) => (
                <DomainMatchCard key={m.accountId} match={m} onViewEdit={() => openPanelForMatch(m)} />
              ))}
            </div>
          ) : null}
        </div>
      ) : orderedMatches != null ? (
        <div className="flex flex-col gap-2">
          {orderedMatches.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              {'No accounts found for this email or domain.'}
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {`Email not found · ${orderedMatches.length} account${orderedMatches.length === 1 ? '' : 's'} matched `}
                <span className="font-mono">{resolved.domainSearched}</span>
              </p>
              {orderedMatches.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((m) => (
                <DomainMatchCard key={m.accountId} match={m} onViewEdit={() => openPanelForMatch(m)} />
              ))}
              {orderedMatches.length > PAGE_SIZE && (
                <div className="flex items-center justify-between pt-1">
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 0}
                  >
                    {'← Prev'}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {`${page + 1} / ${Math.ceil(orderedMatches.length / PAGE_SIZE)}`}
                  </span>
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={(page + 1) * PAGE_SIZE >= orderedMatches.length}
                  >
                    {'Next →'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      <CustomerPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        resolved={panelResolved}
      />
    </section>
  );
}
